import type { components } from '@social-studio/api-client';
import { api } from '../../lib/api';

export type InboxMessage = components['schemas']['InboxMessageDto'];
export type InboxMessageDetail = components['schemas']['InboxMessageDetailDto'];
export type InboxSummary = components['schemas']['InboxSummaryDto'];
export type InboxStatus = InboxMessage['status'];
export type InboxSeverity = InboxMessage['severity'];
export type InboxKind = InboxMessage['kind'];
export type DirectorRun = components['schemas']['DirectorRunDto'];

export interface InboxFilters {
  readonly status: InboxStatus;
  readonly kinds: readonly InboxKind[];
  readonly severities: readonly InboxSeverity[];
}

export interface InboxPage {
  readonly data: InboxMessage[];
  readonly next_cursor: string | null;
}

const PAGE_SIZE = 20;

/** A failed request: the HTTP status (0 = network) and the problem type slug when the API named one. */
export class InboxRequestError extends Error {
  constructor(
    readonly status: number,
    readonly problem: string | null = null,
  ) {
    super(`inbox request answered ${status}${problem ? ` ${problem}` : ''}`);
  }
}

/** The problem type slug of a failed answer (.../problems/message-not-open -> message-not-open), from the body the client already parsed. */
const problemOf = (error: unknown): string | null => {
  const type = (error as { type?: unknown } | null | undefined)?.type;
  return typeof type === 'string' ? (type.split('/').pop() ?? null) : null;
};

async function call<T>(request: () => Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  let result;
  try {
    result = await request();
  } catch {
    throw new InboxRequestError(0);
  }
  if (result.response.ok && result.data !== undefined) return result.data;
  throw new InboxRequestError(result.response.status, problemOf(result.error));
}

/** One page of messages, critical first (the server's order is kept). */
export function fetchInboxPage(cursor: string | undefined, filters: InboxFilters): Promise<InboxPage> {
  return call(() =>
    api.GET('/v1/inbox-messages', {
      params: {
        query: {
          limit: PAGE_SIZE,
          cursor,
          status: [filters.status],
          kind: filters.kinds.length > 0 ? [...filters.kinds] : undefined,
          severity: filters.severities.length > 0 ? [...filters.severities] : undefined,
        },
      },
    }),
  );
}

export function fetchInboxSummary(): Promise<InboxSummary> {
  return call(() => api.GET('/v1/inbox-messages/summary'));
}

export function fetchInboxMessage(id: string): Promise<InboxMessageDetail> {
  return call(() => api.GET('/v1/inbox-messages/{id}', { params: { path: { id } } }));
}

export type InboxActionResult = components['schemas']['InboxActionResultDto'];
/** The actions the client posts; `view` and `choose_media` are navigation and never sent. */
export type InboxServerAction = 'accept' | 'regenerate' | 'create_campaign' | 'ignore';

/**
 * One action of a message (spec 110 section 5). The `Idempotency-Key` is chosen
 * by the caller so a retry repeats it; the answer is 200, or 202 when the action
 * queued an AI run, and always names what it created in `result`.
 */
export function runInboxAction(id: string, type: InboxServerAction, idempotencyKey: string): Promise<InboxActionResult> {
  return call(() =>
    api.POST('/v1/inbox-messages/{id}/actions', {
      params: { path: { id }, header: { 'Idempotency-Key': idempotencyKey } },
      body: { type },
    }),
  );
}

/** `ignore`: see `runInboxAction`. */
export function ignoreInboxMessage(id: string, idempotencyKey: string): Promise<InboxMessage> {
  return runInboxAction(id, 'ignore', idempotencyKey);
}

/** Starts the daily routine now (`ai:run`); 409 `director-run-in-progress` when one is running. */
export function startDailyDirectorRun(): Promise<DirectorRun> {
  return call(() => api.POST('/v1/director-runs', { body: { routine: 'daily' } }));
}

/** The latest run of the daily routine, used to see when a started run has finished. */
export async function fetchLatestDailyRun(): Promise<DirectorRun | null> {
  const page = await call(() => api.GET('/v1/director-runs', { params: { query: { routine: 'daily', limit: 1 } } }));
  return page.data[0] ?? null;
}
